import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/toaster';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PortalAuthProvider, usePortal } from './contexts/PortalAuthContext';
import { PortalLayout } from './components/portal/PortalLayout';
import telemetry from './lib/telemetry';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ClientErrorReporter } from './components/ClientErrorReporter';
import { DebugChatBox } from './components/DebugChatBox';
import { AuthPage } from './pages/AuthPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardLayout } from './components/DashboardLayout';
import { DashboardPage } from './pages/DashboardPage';
import { PipelineOverviewPage } from './pages/PipelineOverviewPage';
import { PipelinePage } from './pages/PipelinePage';
import { DeliverablesPage } from './pages/DeliverablesPage';
import { TasksPage } from './pages/TasksPage';
import { RiskAssessmentPage } from './pages/RiskAssessmentPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { PendingApprovalsPage } from './pages/PendingApprovalsPage';
import { WhatsAppManagementPage } from './pages/WhatsAppManagementPage';
import { RevenueTargetsPage } from './pages/RevenueTargetsPage';
import { ExecutivePerformancePage } from './pages/ExecutivePerformancePage';
import { SettingsPage } from './pages/SettingsPage';
import { LeadImportPage } from './pages/LeadImportPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { CustomFieldManager } from './components/custom-fields/CustomFieldManager';
import { DuplicateListPage } from './pages/DuplicateListPage';
import { DeliverableTemplatesPage } from './pages/DeliverableTemplatesPage';
import { DeliverableAnalyticsDashboard } from './pages/DeliverableAnalyticsDashboard';
import { DeliverableKanbanPage } from './pages/DeliverableKanbanPage';
import { ClientDeliverablesPage } from './pages/ClientDeliverablesPage';
import TargetDashboardPage from './pages/TargetDashboardPage';
import { AutoLoginPage } from './pages/AutoLoginPage';
import { LeadDetailPage } from './pages/LeadDetailPage';
import { FinanceDashboardPage } from './pages/FinanceDashboardPage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
import ClientHealthPage from './pages/ClientHealthPage';
import StageGateConfigPage from './pages/StageGateConfigPage';
import SalesDashboardPage from './pages/SalesDashboardPage';
import { DocumentationPage } from './pages/DocumentationPage';
import ActivityLogsPage from './pages/ActivityLogsPage';
import DailyKPIPage from './pages/DailyKPIPage';
import { ReportIssuePage } from './pages/ReportIssuePage';
import { IssueManagementPage } from './pages/IssueManagementPage';

// Portal pages — lazy loaded to keep main bundle size small (PORTAL-09 / CRACO pitfall prevention)
const PortalLoginPage = React.lazy(() => import('./pages/portal/PortalLoginPage').then(m => ({ default: m.PortalLoginPage })));
const PortalCallbackPage = React.lazy(() => import('./pages/portal/PortalCallbackPage').then(m => ({ default: m.PortalCallbackPage })));
const PortalDashboardPage = React.lazy(() => import('./pages/portal/PortalDashboardPage').then(m => ({ default: m.PortalDashboardPage })));
const PortalDeliverablesPage = React.lazy(() => import('./pages/portal/PortalDeliverablesPage').then(m => ({ default: m.PortalDeliverablesPage })));
const PortalDeliverableDetailPage = React.lazy(() => import('./pages/portal/PortalDeliverableDetailPage').then(m => ({ default: m.PortalDeliverableDetailPage })));
const PortalPaymentsPage = React.lazy(() => import('./pages/portal/PortalPaymentsPage').then(m => ({ default: m.PortalPaymentsPage })));
const PortalInvoicesPage = React.lazy(() => import('./pages/portal/PortalInvoicesPage').then(m => ({ default: m.PortalInvoicesPage })));
const PortalInvoiceDetailPage = React.lazy(() => import('./pages/portal/PortalInvoiceDetailPage').then(m => ({ default: m.PortalInvoiceDetailPage })));

// Public pages — unauthenticated (no JWT, no portal session)
const PublicInvoicePage = React.lazy(() => import('./pages/public/PublicInvoicePage').then(m => ({ default: m.PublicInvoicePage })));

// Invoice admin pages — Wave 3 C3 (authenticated CRM side)
const InvoiceListPage = React.lazy(() => import('./pages/InvoiceListPage').then(m => ({ default: m.InvoiceListPage })));
const InvoiceCreatePage = React.lazy(() => import('./pages/InvoiceCreatePage').then(m => ({ default: m.InvoiceCreatePage })));
const InvoiceDetailPage = React.lazy(() => import('./pages/InvoiceDetailPage').then(m => ({ default: m.InvoiceDetailPage })));
const CompanyProfileSettingsPage = React.lazy(() => import('./pages/CompanyProfileSettingsPage').then(m => ({ default: m.CompanyProfileSettingsPage })));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Portal Route Guard — parallel to ProtectedRoute but uses sessionStorage portalToken
const PortalRoute = ({ children }: { children: React.ReactNode }) => {
  const { portalToken } = usePortal();
  if (!portalToken) {
    return <Navigate to="/client/login" replace />;
  }
  return <>{children}</>;
};

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
};

// Role-based route guard — redirects to dashboard if role not in allowedRoles
const RoleProtectedRoute = ({ children, allowedRoles }: { children: React.ReactNode; allowedRoles: string[] }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  if (!allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// Main App Routes
function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auto-login" element={<AutoLoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Public invoice view — W3-C4. Truly unauthenticated: no JWT, no portal session.
          Token in URL is the invoice.publicAccessToken. */}
      <Route
        path="/public/invoices/:token"
        element={
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" /></div>}>
            <PublicInvoicePage />
          </React.Suspense>
        }
      />

      {/* Protected routes - wrapped with ProtectedRoute */}
      <Route path="/" element={<Navigate to="/sales-dashboard" replace />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline-overview"
        element={
          <ProtectedRoute>
            <PipelineOverviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline"
        element={
          <ProtectedRoute>
            <PipelinePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/import"
        element={
          <ProtectedRoute>
            <LeadImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <ProtectedRoute>
            <LeadDetailPage />
          </ProtectedRoute>
        }
      />
      <Route path="/leads" element={<Navigate to="/pipeline" replace />} />
      <Route
        path="/deliverables"
        element={
          <ProtectedRoute>
            <DeliverablesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deliverable-templates"
        element={
          <ProtectedRoute>
            <DeliverableTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deliverables/analytics"
        element={
          <ProtectedRoute>
            <DeliverableAnalyticsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deliverables/kanban"
        element={
          <ProtectedRoute>
            <DeliverableKanbanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:leadId/deliverables"
        element={
          <ProtectedRoute>
            <ClientDeliverablesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <TasksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/risk-assessment"
        element={
          <ProtectedRoute>
            <RiskAssessmentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp-management"
        element={
          <ProtectedRoute>
            <WhatsAppManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp-management/*"
        element={
          <ProtectedRoute>
            <WhatsAppManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management"
        element={
          <ProtectedRoute>
            <UserManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pending-approvals"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={['CEO', 'GROUP_MANAGER']}>
              <PendingApprovalsPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/targets"
        element={
          <ProtectedRoute>
            <TargetDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/revenue-targets"
        element={
          <ProtectedRoute>
            <RevenueTargetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <ProtectedRoute>
            <FinanceDashboardPage />
          </ProtectedRoute>
        }
      />
      {/* Invoices — Wave 3 C3 */}
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>}>
              <InvoiceListPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/new"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>}>
              <InvoiceCreatePage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:id/edit"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>}>
              <InvoiceCreatePage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>}>
              <InvoiceDetailPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/company"
        element={
          <RoleProtectedRoute allowedRoles={['CEO']}>
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>}>
              <CompanyProfileSettingsPage />
            </React.Suspense>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute>
            <SubscriptionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-health"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={['CEO', 'GROUP_MANAGER', 'MANAGER']}>
              <ClientHealthPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stage-gate-config"
        element={
          <RoleProtectedRoute allowedRoles={['CEO', 'GROUP_MANAGER', 'MANAGER']}>
            <StageGateConfigPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/daily-kpi"
        element={
          <ProtectedRoute>
            <DailyKPIPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-dashboard"
        element={
          <ProtectedRoute>
            <SalesDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executive-performance"
        element={
          <ProtectedRoute>
            <ExecutivePerformancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executive-performance/:id"
        element={
          <ProtectedRoute>
            <ExecutivePerformancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/custom-fields"
        element={
          <ProtectedRoute>
            <CustomFieldManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute>
            <AuditLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/duplicates"
        element={
          <ProtectedRoute>
            <DuplicateListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-logs"
        element={
          <ProtectedRoute>
            <ActivityLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentation"
        element={
          <ProtectedRoute>
            <DocumentationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/report-issue"
        element={
          <ProtectedRoute>
            <ReportIssuePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/issue-management"
        element={
          <RoleProtectedRoute allowedRoles={['CEO', 'GROUP_MANAGER', 'MANAGER']}>
            <IssueManagementPage />
          </RoleProtectedRoute>
        }
      />

      {/* Client Portal routes — outside ProtectedRoute, use PortalRoute guard instead */}
      <Route
        path="/client/login"
        element={
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>}>
            <PortalLoginPage />
          </React.Suspense>
        }
      />
      <Route
        path="/client/callback"
        element={
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>}>
            <PortalCallbackPage />
          </React.Suspense>
        }
      />
      <Route
        path="/client/dashboard"
        element={
          <PortalRoute>
            <React.Suspense fallback={<div />}>
              <PortalLayout>
                <PortalDashboardPage />
              </PortalLayout>
            </React.Suspense>
          </PortalRoute>
        }
      />
      <Route
        path="/client/deliverables/:id"
        element={
          <PortalRoute>
            <React.Suspense fallback={<div />}>
              <PortalLayout>
                <PortalDeliverableDetailPage />
              </PortalLayout>
            </React.Suspense>
          </PortalRoute>
        }
      />
      <Route
        path="/client/deliverables"
        element={
          <PortalRoute>
            <React.Suspense fallback={<div />}>
              <PortalLayout>
                <PortalDeliverablesPage />
              </PortalLayout>
            </React.Suspense>
          </PortalRoute>
        }
      />
      <Route
        path="/client/payments"
        element={
          <PortalRoute>
            <React.Suspense fallback={<div />}>
              <PortalLayout>
                <PortalPaymentsPage />
              </PortalLayout>
            </React.Suspense>
          </PortalRoute>
        }
      />
      <Route
        path="/client/invoices/:id"
        element={
          <PortalRoute>
            <React.Suspense fallback={<div />}>
              <PortalLayout>
                <PortalInvoiceDetailPage />
              </PortalLayout>
            </React.Suspense>
          </PortalRoute>
        }
      />
      <Route
        path="/client/invoices"
        element={
          <PortalRoute>
            <React.Suspense fallback={<div />}>
              <PortalLayout>
                <PortalInvoicesPage />
              </PortalLayout>
            </React.Suspense>
          </PortalRoute>
        }
      />
      <Route path="/client" element={<Navigate to="/client/login" replace />} />

      {/* Fallback - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Syncs auth user with telemetry system
function TelemetryUserSync() {
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      telemetry.setUser({ id: user.id, name: user.name, role: user.role });
    } else {
      telemetry.clearUser();
    }
  }, [user]);

  return null;
}

function App() {
  // Initialize telemetry once on app mount
  useEffect(() => {
    telemetry.init();
    return () => {
      telemetry.destroy();
    };
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TelemetryUserSync />
          <Router>
            <PortalAuthProvider>
              <div className="App">
                <ClientErrorReporter />
                <AppRoutes />
                <Toaster />
                <DebugChatBox />
              </div>
            </PortalAuthProvider>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App; 